import React, { useState, useEffect } from 'react';

const PaymentSettingsAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(true);
  const [paymentKeyInputs, setPaymentKeyInputs] = useState({});
  const [paymentKeySaving, setPaymentKeySaving] = useState({});
  const [showPaymentGuide, setShowPaymentGuide] = useState(false);

  // Load payment settings on mount
  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    setPaymentSettingsLoading(true);
    try {
      const response = await fetch(`${API}/admin/payment-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    } finally {
      setPaymentSettingsLoading(false);
    }
  };

  const savePaymentSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/payment-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(paymentSettings)
      });
      if (response.ok) {
        showNotification && showNotification('Payment settings saved successfully!', 'success');
      } else {
        showNotification && showNotification('Failed to save payment settings', 'error');
      }
    } catch (error) {
      console.error('Error saving payment settings:', error);
      showNotification && showNotification('Error saving payment settings', 'error');
    }
  };

  const handlePaymentToggle = async (provider, enabled) => {
    // Update local state immediately for instant feedback
    const next = {
      ...paymentSettings,
      [provider]: { ...paymentSettings[provider], enabled }
    };
    setPaymentSettings(next);
    // Auto-persist so user doesn't need to remember the bottom save button
    try {
      const r = await fetch(`${API}/admin/payment-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(next),
      });
      if (!r.ok) throw new Error('Failed');
      showNotification && showNotification(`${provider} ${enabled ? 'enabled' : 'disabled'}`, 'success');
    } catch {
      // Revert on failure so the UI matches reality
      setPaymentSettings(paymentSettings);
      showNotification && showNotification('Failed to save toggle', 'error');
    }
  };

  const handlePaymentSettingChange = async (provider, field, value) => {
    const prev = paymentSettings;
    const next = {
      ...paymentSettings,
      [provider]: { ...paymentSettings[provider], [field]: value }
    };
    setPaymentSettings(next);
    try {
      const r = await fetch(`${API}/admin/payment-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(next),
      });
      if (!r.ok) throw new Error('Failed');
      showNotification && showNotification(`${provider} ${field} → ${value}`, 'success');
    } catch {
      setPaymentSettings(prev);
      showNotification && showNotification(`Failed to save ${field}`, 'error');
    }
  };

  const savePaymentKey = async (provider, keyName) => {
    const inputKey = `${provider}_${keyName}`;
    const value = paymentKeyInputs[inputKey];
    if (!value || !value.trim()) {
      showNotification && showNotification('Please enter a key value.', 'error');
      return;
    }
    setPaymentKeySaving(prev => ({ ...prev, [inputKey]: true }));
    try {
      const response = await fetch(`${API}/admin/payment-secrets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ provider, key_name: keyName, key_value: value.trim() })
      });
      if (response.ok) {
        showNotification && showNotification(`${provider} ${keyName} saved securely!`, 'success');
        setPaymentKeyInputs(prev => ({ ...prev, [inputKey]: '' }));
        await loadPaymentSettings();
      } else {
        const err = await response.json();
        showNotification && showNotification(err.detail || 'Failed to save key', 'error');
      }
    } catch (error) {
      showNotification && showNotification('Error saving key', 'error');
    } finally {
      setPaymentKeySaving(prev => ({ ...prev, [inputKey]: false }));
    }
  };

  const removePaymentKey = async (provider, keyName) => {
    const inputKey = `${provider}_${keyName}`;
    setPaymentKeySaving(prev => ({ ...prev, [inputKey]: true }));
    try {
      const response = await fetch(`${API}/admin/payment-secrets/${provider}/${keyName}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        showNotification && showNotification(`${provider} ${keyName} removed.`, 'success');
        await loadPaymentSettings();
      } else {
        showNotification && showNotification('Failed to remove key', 'error');
      }
    } catch (error) {
      showNotification && showNotification('Error removing key', 'error');
    } finally {
      setPaymentKeySaving(prev => ({ ...prev, [inputKey]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Payment Settings
        </h3>
      </div>

      {/* Setup Guide */}
      <div className={`rounded-xl border ${isDarkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}>
        <button
          onClick={() => setShowPaymentGuide(!showPaymentGuide)}
          className={`w-full flex items-center justify-between p-4 text-left rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
        >
          <div className="flex items-center gap-3">
            <span className="text-xl">📖</span>
            <div>
              <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Setup Guide</h4>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>How to connect Stripe & PayPal to your website</p>
            </div>
          </div>
          <span className={`text-lg transition-transform ${showPaymentGuide ? 'rotate-90' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>▶</span>
        </button>

        {showPaymentGuide && (
          <div className={`px-4 pb-4 space-y-5 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {/* Security */}
            <div className="pt-4">
              <h5 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                🔒 Security — How Payments Work
              </h5>
              <ul className={`text-sm space-y-1 ml-6 list-disc ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Your website <strong>NEVER</strong> sees credit card numbers or PayPal passwords</li>
                <li>Users are redirected to Stripe.com / PayPal.com to pay securely</li>
                <li>Payment confirmation comes back via a secure webhook</li>
                <li>Your API keys are encrypted with AES (Fernet) before being stored</li>
                <li>No PCI compliance needed on your side</li>
              </ul>
            </div>

            {/* Stripe Steps */}
            <div>
              <h5 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                Stripe Setup (Credit/Debit Cards)
              </h5>
              <ol className={`text-sm space-y-1 ml-6 list-decimal ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Go to <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">dashboard.stripe.com/register</a> — create an account</li>
                <li>Go to <strong>Developers → API Keys</strong></li>
                <li>Copy <strong>"Publishable key"</strong> (starts with <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>pk_test_</code> or <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>pk_live_</code>)</li>
                <li>Copy <strong>"Secret key"</strong> (starts with <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>sk_test_</code> or <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>sk_live_</code>)</li>
                <li>Go to <strong>Developers → Webhooks → Add endpoint</strong></li>
                <li>Set endpoint URL to: <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>https://yourdomain.com/api/webhooks/stripe</code></li>
                <li>Select event: <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>checkout.session.completed</code></li>
                <li>Copy the <strong>"Signing secret"</strong> (starts with <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>whsec_</code>)</li>
                <li>Paste all 3 keys in the Stripe fields below</li>
                <li>Enable the Stripe toggle → click <strong>Save</strong></li>
              </ol>
            </div>

            {/* PayPal Steps */}
            <div>
              <h5 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                PayPal Setup
              </h5>
              <ol className={`text-sm space-y-1 ml-6 list-decimal ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Go to <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">developer.paypal.com/dashboard/applications</a></li>
                <li>Create a new app (or use the default one)</li>
                <li>Copy <strong>"Client ID"</strong> and <strong>"Client Secret"</strong></li>
                <li>Go to <strong>Webhooks → Add webhook</strong></li>
                <li>Set URL to: <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>https://yourdomain.com/api/webhooks/paypal</code></li>
                <li>Select event: <code className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>PAYMENT.CAPTURE.COMPLETED</code></li>
                <li>Copy the <strong>"Webhook ID"</strong></li>
                <li>Paste all 3 keys in the PayPal fields below</li>
                <li>Enable the PayPal toggle → click <strong>Save</strong></li>
              </ol>
            </div>

            {/* Test vs Live */}
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700/40' : 'bg-yellow-50 border border-yellow-200'}`}>
              <h5 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                ⚠️ Test vs Live Mode
              </h5>
              <ul className={`text-sm space-y-1 ml-6 list-disc ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Use <strong>TEST / SANDBOX</strong> mode first to verify everything works</li>
                <li>Switch to <strong>LIVE</strong> mode only when ready to accept real payments</li>
                <li>You need <strong>LIVE keys</strong> from Stripe/PayPal — test keys won't work in live mode</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Loading state */}
      {paymentSettingsLoading || !paymentSettings ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading payment settings...</p>
          </div>
        </div>
      ) : (
      <>
        {/* Payment Providers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stripe Configuration */}
          <div className={`p-6 rounded-xl border-2 ${
            isDarkMode
              ? 'bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border-purple-500/30'
              : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-300'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white font-bold">S</span>
              </div>
              <div>
                <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Stripe</h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Credit/Debit Cards</p>
              </div>
              <div className="ml-auto">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={paymentSettings?.stripe?.enabled || false}
                    onChange={(e) => handlePaymentToggle('stripe', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>

            <div className="space-y-3">
              {[
                { label: 'Publishable Key', keyName: 'publishable_key', setField: 'publishable_key_set', maskedField: 'publishable_key_masked', placeholder: 'pk_test_...' },
                { label: 'Secret Key', keyName: 'secret_key', setField: 'secret_key_set', maskedField: 'secret_key_masked', placeholder: 'sk_test_...' },
                { label: 'Webhook Secret', keyName: 'webhook_secret', setField: 'webhook_secret_set', maskedField: 'webhook_secret_masked', placeholder: 'whsec_...' }
              ].map(({ label, keyName, setField, maskedField, placeholder }) => (
                <div key={keyName}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</span>
                    {paymentSettings?.stripe?.[setField] && (
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        ({paymentSettings?.stripe?.[maskedField]})
                      </span>
                    )}
                  </div>
                  {paymentSettings?.stripe?.[setField] ? (
                    <button
                      onClick={() => removePaymentKey('stripe', keyName)}
                      disabled={paymentKeySaving[`stripe_${keyName}`]}
                      className="text-xs px-3 py-1 rounded bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-50"
                    >
                      {paymentKeySaving[`stripe_${keyName}`] ? 'Removing...' : 'Remove Key'}
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="password"
                        placeholder={placeholder}
                        value={paymentKeyInputs[`stripe_${keyName}`] || ''}
                        onChange={(e) => setPaymentKeyInputs(prev => ({ ...prev, [`stripe_${keyName}`]: e.target.value }))}
                        className={`flex-1 text-xs px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      />
                      <button
                        onClick={() => savePaymentKey('stripe', keyName)}
                        disabled={paymentKeySaving[`stripe_${keyName}`] || !paymentKeyInputs[`stripe_${keyName}`]}
                        className="text-xs px-3 py-1.5 rounded bg-purple-600 hover:bg-purple-700 text-white font-medium disabled:opacity-50"
                      >
                        {paymentKeySaving[`stripe_${keyName}`] ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Keys are encrypted before storage. You can also set keys in the .env file as fallback.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <span className={`text-xs px-2 py-1 rounded ${
                  paymentSettings?.stripe?.mode === 'live'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                }`}>
                  {paymentSettings?.stripe?.mode === 'live' ? 'LIVE MODE' : 'TEST MODE'}
                </span>
                <button
                  onClick={() => handlePaymentSettingChange('stripe', 'mode', paymentSettings?.stripe?.mode === 'live' ? 'test' : 'live')}
                  className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                >
                  Switch Mode
                </button>
              </div>
            </div>
          </div>

          {/* PayPal Configuration */}
          <div className={`p-6 rounded-xl border-2 ${
            isDarkMode
              ? 'bg-gradient-to-br from-blue-900/40 to-cyan-900/40 border-blue-500/30'
              : 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-300'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white font-bold">P</span>
              </div>
              <div>
                <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>PayPal</h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>PayPal & Venmo</p>
              </div>
              <div className="ml-auto">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={paymentSettings?.paypal?.enabled || false}
                    onChange={(e) => handlePaymentToggle('paypal', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <div className="space-y-3">
              {[
                { label: 'Client ID', keyName: 'client_id', setField: 'client_id_set', maskedField: 'client_id_masked', placeholder: 'Client ID from PayPal dashboard' },
                { label: 'Client Secret', keyName: 'client_secret', setField: 'client_secret_set', maskedField: 'client_secret_masked', placeholder: 'Client Secret from PayPal dashboard' },
                { label: 'Webhook ID', keyName: 'webhook_id', setField: 'webhook_id_set', maskedField: 'webhook_id_masked', placeholder: 'Webhook ID from PayPal dashboard' }
              ].map(({ label, keyName, setField, maskedField, placeholder }) => (
                <div key={keyName}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</span>
                    {paymentSettings?.paypal?.[setField] && (
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        ({paymentSettings?.paypal?.[maskedField]})
                      </span>
                    )}
                  </div>
                  {paymentSettings?.paypal?.[setField] ? (
                    <button
                      onClick={() => removePaymentKey('paypal', keyName)}
                      disabled={paymentKeySaving[`paypal_${keyName}`]}
                      className="text-xs px-3 py-1 rounded bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-50"
                    >
                      {paymentKeySaving[`paypal_${keyName}`] ? 'Removing...' : 'Remove Key'}
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="password"
                        placeholder={placeholder}
                        value={paymentKeyInputs[`paypal_${keyName}`] || ''}
                        onChange={(e) => setPaymentKeyInputs(prev => ({ ...prev, [`paypal_${keyName}`]: e.target.value }))}
                        className={`flex-1 text-xs px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      />
                      <button
                        onClick={() => savePaymentKey('paypal', keyName)}
                        disabled={paymentKeySaving[`paypal_${keyName}`] || !paymentKeyInputs[`paypal_${keyName}`]}
                        className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
                      >
                        {paymentKeySaving[`paypal_${keyName}`] ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Keys are encrypted before storage. You can also set keys in the .env file as fallback.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <span className={`text-xs px-2 py-1 rounded ${
                  paymentSettings?.paypal?.mode === 'live'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                }`}>
                  {paymentSettings?.paypal?.mode === 'live' ? 'LIVE MODE' : 'SANDBOX MODE'}
                </span>
                <button
                  onClick={() => handlePaymentSettingChange('paypal', 'mode', paymentSettings?.paypal?.mode === 'live' ? 'sandbox' : 'live')}
                  className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                >
                  Switch Mode
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* General Payment Settings */}
        <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            General Settings
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Default Currency
              </label>
              <select
                value={paymentSettings?.currency || 'USD'}
                onChange={(e) => setPaymentSettings({...paymentSettings, currency: e.target.value})}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (Euro)</option>
                <option value="GBP">GBP (Pound)</option>
                <option value="MAD">MAD (Dirham)</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tax Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={paymentSettings?.tax_rate || ''}
                onChange={(e) => setPaymentSettings({...paymentSettings, tax_rate: parseFloat(e.target.value) || 0})}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Payment Disclaimer Settings */}
        <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Payment Disclaimer (Membership Page)
            </h4>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={paymentSettings?.disclaimer?.enabled || false}
                onChange={(e) => setPaymentSettings({
                  ...paymentSettings,
                  disclaimer: { ...paymentSettings?.disclaimer, enabled: e.target.checked }
                })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              <span className={`ml-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {paymentSettings?.disclaimer?.enabled ? 'Visible' : 'Hidden'}
              </span>
            </label>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Disclaimer Text
            </label>
            <textarea
              rows={3}
              placeholder="Enter payment disclaimer text that will appear on the Membership page..."
              value={paymentSettings?.disclaimer?.text || ''}
              onChange={(e) => setPaymentSettings({
                ...paymentSettings,
                disclaimer: { ...paymentSettings?.disclaimer, text: e.target.value }
              })}
              className={`w-full px-3 py-2 rounded-lg border ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              This disclaimer will be shown at the bottom of the Membership page to inform users about payment processing.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <button
            onClick={loadPaymentSettings}
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Reset
          </button>
          <button
            onClick={savePaymentSettings}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <span>Save Payment Settings</span>
          </button>
        </div>

        {/* Security Notice */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
            <strong>Security Notice:</strong> All API keys are encrypted with Fernet (AES) before storage in the database. Keys are never shown in plaintext after saving. You can also configure keys via the .env file on the server — DB keys take priority over .env.
          </p>
        </div>
      </>
      )}
    </div>
  );
};

export default PaymentSettingsAdmin;
